import { useMemberFieldsWithSources, useOrderedMemberColumns } from '@frontend/app/hooks/memberList';
import { useMemo } from 'react';
import { includes } from 'lodash';
import { useParams } from 'react-router-dom';
import { useMemberListContext } from '@frontend/app/context/MemberListContext';
import { createApplicationTemplateColumns } from '@frontend/app/components/MemberTable/utils/createApplicationTemplateColumns';
import { useMemberFieldSchemasQuery } from '@frontend/app/hooks';
import { TProject } from '../../../types';

interface IProspectListColumn {
  field: string;
  headerName: string;
}

interface IProspectListColumnsProps {
  project: TProject;
}

export const useProspectListColumns = ({ project }: IProspectListColumnsProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const programId = projectId ? parseInt(projectId, 10) : undefined;

  const {
    columns: columnsContext,
    segment,
  } = useMemberListContext();

  const columns = useOrderedMemberColumns(columnsContext, segment);

  const { fields: fieldsWithSources } = useMemberFieldsWithSources(undefined, programId, true);

  const { data: { schemas: memberFieldSchemas = null } = {} } = useMemberFieldSchemasQuery();

  const socialMediaFields = useMemo(() => {
    const socialFieldNames = ['Instagram', 'TikTok', 'YouTube', 'Twitter', 'Facebook', 'LinkedIn', 'Snapchat'];
    return fieldsWithSources.filter((field) =>
      includes(socialFieldNames, field.headerName) &&
      field.source === 'Default Member Data'
    );
  }, [fieldsWithSources]);

  return useMemo(() => {
    const processedColumns: IProspectListColumn[] = [];

    // Create a set of social media field IDs to filter out from regular columns
    const socialFieldIds = new Set(socialMediaFields.map((field) => field.field));

    columns.forEach((column) => {
      // Skip social media fields as they'll be handled by the social column
      if (socialFieldIds.has(column.field)) {
        return;
      }

      // Add the regular column
      processedColumns.push({
        field: column.field,
        headerName: column.headerName,
      });

      // Add social column after name column
      if (column.field === 'name' && socialMediaFields.length > 0) {
        socialMediaFields.forEach((field) => {
          processedColumns.push({
            field: field.field,
            headerName: field.headerName,
          });
        });
      }
    });

    const applicationTemplateColumns: IProspectListColumn[] = createApplicationTemplateColumns(project, columns, memberFieldSchemas, true);

    return [...processedColumns, ...applicationTemplateColumns];
  }, [columns, memberFieldSchemas, project, socialMediaFields]);
};
